// Recepción y descifrado de mensajes — modelo simple alineado con la landing.
//
// sealed_box decrypt con la priv X25519 de la cuenta efectiva. Sin X3DH, sin ratchet.
// El sender_envelope es otro sealed_box(senderId, miPub) — cualquier device del usuario,
// con la priv compartida, puede descifrar todo.
//
// Cursor del inbox vive en IndexedDB local — no en server.

import * as crypto from '../crypto';
import { api, type IncomingBlob } from '../api';
import * as local from './storage_local';
import * as archive from './conv_archive';

export interface ReceiveContext {
  myPriv: Uint8Array;
  myPub: Uint8Array;
  mySyphraxId: string;            // syphrax_id efectivo (real o pseudonym)
  realSyphraxId: string;
  pseudoFreqLabel: string;
  archiveMasterKey: Uint8Array;
}

export interface DecryptedMessage {
  blobId: string;
  messageId: string | null;
  senderSyphraxId: string;
  senderDeviceId: string;          // siempre '' (no hay devices). Mantenido por compat.
  type: 'msg' | 'media' | 'ctrl';
  plaintext: string;
  createdAt: string;
}

const cursorKey = (mySyphraxId: string) => 'inboxCursor:' + mySyphraxId;

async function ingestOne(ctx: ReceiveContext, blob: IncomingBlob): Promise<DecryptedMessage | null> {
  // sender_envelope = sealed_box(senderId, miPub).
  if (!blob.sender_envelope) return null;
  let senderSyphraxId: string;
  try {
    senderSyphraxId = crypto.sealedBoxDecrypt(
      crypto.b64decode(blob.sender_envelope),
      ctx.myPub,
      ctx.myPriv,
    );
  } catch (_e) {
    return null;  // No es para mí, o envelope corrupto.
  }

  // ciphertext = sealed_box(plaintext, miPub).
  let plaintext: string;
  try {
    plaintext = crypto.sealedBoxDecrypt(
      crypto.b64decode(blob.ciphertext),
      ctx.myPub,
      ctx.myPriv,
    );
  } catch (err) {
    console.warn('[messaging] sealed_box decrypt failed:', (err as Error).message, 'from', senderSyphraxId);
    return null;
  }

  return {
    blobId: blob.id,
    messageId: blob.message_id ?? null,
    senderSyphraxId,
    senderDeviceId: '',
    type: blob.type as 'msg' | 'media' | 'ctrl',
    plaintext,
    createdAt: blob.created_at,
  };
}

export async function fetchInbox(ctx: ReceiveContext): Promise<DecryptedMessage[]> {
  const ck = cursorKey(ctx.mySyphraxId);
  const since = await local.getCursor(ck);
  const myReceiverTag = crypto.generalTag(ctx.mySyphraxId);
  const tagB64 = crypto.b64encode(myReceiverTag);
  const r = await api.fetchMessages({ receiver_tag: tagB64, freq_tag: null, since: since ?? undefined });

  const out: DecryptedMessage[] = [];
  let maxAt = since;
  for (const m of r.messages) {
    try {
      const dec = await ingestOne(ctx, m);
      if (dec) {
        out.push(dec);
        // Write-through al archive del receiver. Solo para mensajes ENTRANTES (de otro).
        // Self-copies del sender ya se escriben en su propio archive desde send.ts.
        if (dec.messageId && (dec.type === 'msg' || dec.type === 'media')) {
          const fromMe = dec.senderSyphraxId === ctx.realSyphraxId;
          if (!fromMe) {
            // Awaited: el chunk debe existir en BD antes de que el caller pueda
            // intentar updateChunkFlags(read=true). En fire-and-forget se perdían
            // las marcas de leído porque el endpoint UPDATE devolvía 404.
            try {
              await archive.writeArchiveChunk(
                ctx.archiveMasterKey, ctx.realSyphraxId, dec.senderSyphraxId, ctx.pseudoFreqLabel,
                { messageId: dec.messageId,
                  conversationKey: { peerSyphraxId: dec.senderSyphraxId, freqLabel: ctx.pseudoFreqLabel },
                  fromMe: false, fromDeviceId: null,
                  kind: dec.type, body: dec.plaintext,
                  createdAt: dec.createdAt, delivered: true, op: 'append' },
              );
            } catch { /* best-effort */ }
          }
        }
      }
      if (!maxAt || m.created_at > maxAt) maxAt = m.created_at;
    } catch (_e) { /* siguiente */ }
  }
  if (maxAt && maxAt !== since) await local.setCursor(ck, maxAt);
  return out;
}

export async function ingestLive(ctx: ReceiveContext, blob: IncomingBlob): Promise<DecryptedMessage | null> {
  const result = await ingestOne(ctx, blob);
  const ck = cursorKey(ctx.mySyphraxId);
  const cur = await local.getCursor(ck);
  if (!cur || blob.created_at > cur) await local.setCursor(ck, blob.created_at);
  if (result && result.messageId && (result.type === 'msg' || result.type === 'media')) {
    const fromMe = result.senderSyphraxId === ctx.realSyphraxId;
    if (!fromMe) {
      try {
        await archive.writeArchiveChunk(
          ctx.archiveMasterKey, ctx.realSyphraxId, result.senderSyphraxId, ctx.pseudoFreqLabel,
          { messageId: result.messageId,
            conversationKey: { peerSyphraxId: result.senderSyphraxId, freqLabel: ctx.pseudoFreqLabel },
            fromMe: false, fromDeviceId: null,
            kind: result.type, body: result.plaintext,
            createdAt: result.createdAt, delivered: true, op: 'append' },
        );
      } catch { /* best-effort */ }
    }
  }
  return result;
}
